'use client';

import sendAiContentDownloadEvent from 'components/components/events/AiContentDownloadEvent';
import { type JSX, useCallback, useEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { Slider, Text } from '@videoblocks/design-system';
import { AiContentVoiceoverType } from '@videoblocks/events-ts/lib/storyblocks/content/ai/AiContentVoiceoverTypeEnum';
import { Bin, DownloadBottom, Pause, Play } from '@videoblocks/react-icons';

import { dispatchAudioPlay, generateAudioInstanceId } from '../_hooks/AudioPlayEvent';
import { useAudioCoordination } from '../_hooks/useAudioCoordination';

export type GeneratedVoicePlayerProps = {
    audioUrl: string;
    title: string;
    header?: string;
    subtitle?: string;
    onDelete?: () => void;
    script?: string | null;
    hasPlayed: boolean;
    setHasPlayed: () => void;
    // event tracking properties
    aiContentId?: string | null;
    lastAiContentId?: string | null;
    voiceId?: string | null;
    aiContentVoiceoverType?: keyof typeof AiContentVoiceoverType;
    aiModelName?: string | null;
};

const GeneratedVoicePlayer = ({
    audioUrl,
    title,
    header,
    subtitle,
    onDelete,
    script,
    hasPlayed,
    setHasPlayed,
    aiContentId,
    lastAiContentId,
    voiceId,
    aiContentVoiceoverType,
    aiModelName,
}: GeneratedVoicePlayerProps): JSX.Element => {
    const audioRef = useRef<HTMLAudioElement>(null);
    const animationFrameRef = useRef<number | null>(null);
    // Generate a stable unique instance ID for audio coordination
    const instanceIdRef = useRef(generateAudioInstanceId());

    const [isPlaying, _setIsPlaying] = useState(false);
    const [duration, setDuration] = useState(0);
    const [currentTime, setCurrentTime] = useState(0);
    const [isLoading, setIsLoading] = useState(true);

    const setIsPlaying = (playing: boolean): void => {
        if (playing) {
            dispatchAudioPlay(audioUrl, instanceIdRef.current);
        }
        _setIsPlaying(playing);
    };

    const handlePause = useCallback((): void => {
        if (audioRef.current) {
            audioRef.current.pause();
        }
        // Call _setIsPlaying directly to avoid stale closure with setIsPlaying wrapper
        // No need to dispatch AudioPlayEvent when pausing (only when playing)
        _setIsPlaying(false);
    }, []);

    // Pass the ref object (not .current) so useAudioCoordination reads it inside effects
    useAudioCoordination({
        instanceIdRef,
        onPause: handlePause,
    });

    const progressPercentage = duration ? (currentTime / duration) * 100 : 0;

    const updateProgress = () => {
        if (!audioRef.current || !isPlaying) return;

        const audio = audioRef.current;

        setCurrentTime(audio.currentTime);

        animationFrameRef.current = requestAnimationFrame(updateProgress);
    };

    const togglePlayPause = () => {
        if (!audioRef.current) return;

        if (isPlaying) {
            audioRef.current.pause();
        } else {
            void audioRef.current.play();
        }
        setIsPlaying(!isPlaying);
    };

    const generateDownloadFilename = () => {
        const filenameComponents = [title];
        if (script) {
            filenameComponents.push(script.substring(0, 15).replace(/\s+/g, '_') + '...');
        }

        return `${filenameComponents.join('_')}.mp3`;
    };

    const handleDownload = () => {
        const link = document.createElement('a');
        link.href = audioUrl;
        link.download = generateDownloadFilename();
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        if (aiContentId && aiContentVoiceoverType) {
            void sendAiContentDownloadEvent(
                aiContentId,
                voiceId ?? null,
                aiContentVoiceoverType,
                window.location.href,
                document.referrer || undefined,
                lastAiContentId ?? null,
                aiModelName ?? null,
            );
        }
    };

    const handleSliderChange = (newValue: number[]) => {
        if (audioRef.current) {
            const newTime = (newValue[0] / 100) * duration;
            audioRef.current.currentTime = newTime;
            setCurrentTime(newTime);
        }
    };

    useEffect(() => {
        if (isPlaying) {
            animationFrameRef.current = requestAnimationFrame(updateProgress);
        } else {
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
        }

        return () => {
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
        };
    }, [isPlaying, duration]);

    // Auto-play when URL changes
    useEffect(() => {
        if (audioUrl && audioRef.current) {
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setIsLoading(true);
            setCurrentTime(0);

            audioRef.current.load();

            const handleCanPlay = () => {
                if (audioRef.current) {
                    if (!hasPlayed) {
                        audioRef.current
                            .play()
                            .then(() => {
                                setIsPlaying(true);
                            })
                            .catch((error) => {
                                console.log('Auto-play prevented by browser:', error);
                                setIsPlaying(false);
                            });
                    }

                    setIsLoading(false);
                }
            };

            audioRef.current.addEventListener('canplay', handleCanPlay, { once: true });

            return () => {
                if (audioRef.current) {
                    audioRef.current.removeEventListener('canplay', handleCanPlay);
                }
            };
        }
    }, [audioUrl, hasPlayed]);

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        const handleLoadedMetadata = () => {
            if (audio) {
                setDuration(audio.duration);
            }
        };

        const handleEnded = () => {
            setIsPlaying(false);
            setHasPlayed?.();
            setCurrentTime(0);
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
        };

        const handleLoadStart = () => {
            setIsLoading(true);
        };

        audio.addEventListener('loadedmetadata', handleLoadedMetadata);
        audio.addEventListener('ended', handleEnded);
        audio.addEventListener('loadstart', handleLoadStart);

        return () => {
            audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
            audio.removeEventListener('ended', handleEnded);
            audio.removeEventListener('loadstart', handleLoadStart);
        };
    }, []);

    useEffect(() => {
        return () => {
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
        };
    }, []);

    return (
        <div className="w-full mx-auto mt-8">
            <audio ref={audioRef} src={audioUrl} preload="metadata" />

            <div
                className={twMerge(
                    'bg-gray-900 pb-10 rounded-xl p-4 border-2 border-white',
                    header && 'pb-[38px]', // this ensures the main content is centered by adding the bottom padding + header text height
                )}
            >
                {header && (
                    <Text fontSize="base" fontWeight="regular" className="">
                        {header}
                    </Text>
                )}
                <div className="flex items-center gap-4">
                    <button
                        onClick={togglePlayPause}
                        disabled={isLoading}
                        data-testid="play-pause"
                        className={twMerge(
                            'flex-shrink-0 w-12 h-12 hover:bg-blue-600 rounded-full flex items-center justify-center text-white transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer',
                            isPlaying ? 'bg-blue-500' : 'bg-gray-400',
                        )}
                    >
                        {isLoading ? (
                            <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                        ) : isPlaying ? (
                            <Pause className="w-6 h-6 fill-current" />
                        ) : (
                            <Play className="w-6 h-6 fill-current ml-0.5" />
                        )}
                    </button>

                    <div className="flex-shrink-0 min-w-0">
                        <h3 className="text-white font-medium text-sm truncate">{title}</h3>
                        <p className="text-gray-400 text-xs truncate">{subtitle}</p>
                    </div>

                    <Slider
                        value={[progressPercentage]}
                        onValueChange={handleSliderChange}
                        className="mx-4"
                        trackProps={{
                            className: 'bg-white',
                        }}
                        rangeProps={{
                            className: 'bg-blue-600',
                        }}
                    />

                    {!onDelete ? (
                        <button
                            onClick={handleDownload}
                            className="flex-shrink-0 w-10 h-10 bg-yellow-400 hover:bg-yellow-500 rounded-full flex items-center justify-center text-white transition-all duration-200 transform cursor-pointer hover:scale-105"
                            title="Download audio"
                        >
                            <DownloadBottom className="w-4 h-4" />
                        </button>
                    ) : (
                        <button
                            onClick={onDelete}
                            className="flex-shrink-0 w-10 h-10 bg-yellow-400 hover:bg-yellow-500 rounded-full flex items-center justify-center text-white transition-all duration-200 transform cursor-pointer hover:scale-105"
                            title="Delete audio"
                        >
                            <Bin className="w-4 h-4" />
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default GeneratedVoicePlayer;
